import { QueryFunctionContext } from '@tanstack/react-query';
import { DataObjectOutputType, DataObjectType } from '../../../types';
import {
  AUTOCOMPLETE_PAGE_SIZE,
  OptionsPage,
  OptionType,
  Page,
} from '../../../../common/queryClient';
import { apiClient } from '../../../../common/ApiClient';
import { expand } from 'uri-template-lite';
import { DataObjectUrl } from '../../../query/DataObjectUrl';
import { useInfiniteSearchQuery } from '../../../../common/queryClient/useInfiniteSearchQuery';
import { formatCodeName } from '../../../../common/utils';
import { AlgorithmSourceDataObjectDescriptionFormDataType } from '../schema/types';

export interface AlgorithmSourceOutSourceSelectOption extends OptionType {
  code?: string;
  name?: string;
  dataObjectType?: DataObjectType;
  outputTypeCode?: DataObjectOutputType;
  dataObjectDescription?: AlgorithmSourceDataObjectDescriptionFormDataType;
  domainId?: number;
}

export interface DataObjectForAlgorithmSourceSearchQueryParams {
  searchText: string;
  includeDataObjectIds?: number[];
  dataObjectType?: DataObjectType;
  outputTypeCodes?: DataObjectOutputType[];
  outputDataObjectId?: number;
  domainId?: number;
  page?: number;
  size?: number;
}

export interface DataObjectForSourceSearchOptions {
  includeDataObjectIds?: number[];
  dataObjectType?: DataObjectType;
  outputTypeCodes?: DataObjectOutputType[];
  outputDataObjectId?: number;
  domainId?: number;
}

const searchDataObjectsForAlgorithmSource = ({
  searchText,
  includeDataObjectIds,
  dataObjectType,
  outputTypeCodes,
  outputDataObjectId,
  domainId,
  page,
  size,
}: DataObjectForAlgorithmSourceSearchQueryParams) =>
  apiClient
    .get<Page<AlgorithmSourceDataObjectDescriptionFormDataType>>(
      expand(DataObjectUrl.SEARCH_FOR_ALGORITHM_SOURCE, {
        searchText,
        includeDataObjectIds,
        dataObjectType,
        outputTypeCodes,
        outputDataObjectId,
        domainId,
        page,
        size,
      })
    )
    .then((response) => response.data);

const searchAlgorithmSourceObjectOptions = async ({
  meta,
  pageParam,
}: QueryFunctionContext<any, number>): Promise<OptionsPage> => {
  const page = pageParam ?? 0;
  const params = meta as
    | Partial<DataObjectForAlgorithmSourceSearchQueryParams>
    | undefined;
  const result = await searchDataObjectsForAlgorithmSource({
    ...params,
    searchText: (meta?.searchText ?? '') as string,
    page,
    size: AUTOCOMPLETE_PAGE_SIZE,
  });

  const content: OptionType[] = result.content.map((item) => {
    return {
      code: item.code,
      name: item.name,
      domainId: item.domainId || undefined,
      dataObjectType: item.dataObjectType,
      outputTypeCode: item.outputTypeCode,
      value: 'dod-' + item.id,
      label: formatCodeName(item),
      dimensions: item.dimensions,
    } as AlgorithmSourceOutSourceSelectOption;
  });
  return {
    content,
    last: result.last,
    number: result.number,
  };
};

export function useInfiniteSearchAlgorithmSourceObjectsQuery(
  term: string,
  options?: DataObjectForSourceSearchOptions
) {
  return useInfiniteSearchQuery(
    ['search-algorithm-source-objects-autocomplete'],
    searchAlgorithmSourceObjectOptions,
    term,
    options
  );
}
